/** @jsx jsx */
import { Box, Button, Inline, jsx, Label, Stack, StyleProp } from '@front10/base-elements';
import { Icon, IconName } from '../../packages/base-elements/Icon';
import { useCombobox } from 'downshift';
import VisuallyHidden from '@reach/visually-hidden';
import * as React from 'react';
import { Fragment, useEffect, useState } from 'react';
import { useVirtual } from 'react-virtual';
import { Input } from './Input';
import { MobileFilterDialog, MobileHeader, MobileFooter, MobileBody } from './MobileFilterView';
import { noop } from './utils';
import { useTranslation, useIsMobileView } from '@front10/utils';
import nanoid from 'nanoid';

const styles = {
  menu: {
    overflowY: 'auto',
    padding: 0,
    margin: 0,
    listStyle: 'none',
    position: 'relative'
  }
} as const;

function getItems<T extends Value>(data: Array<T>, search: string, minSearchLength?: number) {
  if (minSearchLength && minSearchLength > search.length) {
    return [];
  }
  return data.filter((n) => n.label.toLowerCase().includes(search.toLowerCase()));
}

export interface Value {
  value: string;
  label: string;
}

interface Props<T extends Value> {
  label: string;
  placeholder?: string;
  disabled?: boolean;
  defaultValue?: string;
  options: Array<T>;
  onChange?: <V extends Value>(value: V) => void;
  onKeyDown?: (value: any) => void;
  sx?: StyleProp;
  className?: string;
  itemHeight?: number;
  errorMessage?: React.ReactElement;
  hasError?: boolean;
  minCharactersCountToOpen?: number;
  rightIcon?: IconName;
  rightIconOpen?: IconName;
  iconClose?: IconName;
  ariaLabelIconClose?: string;
  descriptionIconCloseAction?: string;
  ariaLabelIconToggle?: string;
  title?: string;
  openOnFocus?: boolean;
  active?: boolean;
  selectedItem?: any;
  isPrepopulatedValue?: boolean;
  wasSetedPrepopulatedValue?: boolean;
  setWasSetedPrepopulatedValue?: (value: any) => void;
}

const KEY_TAB = 9;
export function Combobox<T extends Value>(props: Props<T>) {
  const [isDefaultSet, updateDefaultSet] = useState(false);
  const [inputValue, setInputValue] = useState('');
  const [saveInputValue, setSaveInputValue] = useState('');
  const [saveSelectedItem, setSaveSelectedItem] = useState<any>(undefined);
  const [wasMakedCleanAction, setWasMakedCleanAction] = useState(false);
  const [isConfirmedAction, setIsConfirmedAction] = useState(false);

  const onChange = props.onChange ?? noop;
  const translate = useTranslation();
  const [id] = React.useState(nanoid());

  function stateReducer(_: any, actionAndChanges: any) {
    const { type, changes } = actionAndChanges;
    switch (type) {
      case useCombobox.stateChangeTypes.FunctionReset:
      case useCombobox.stateChangeTypes.InputBlur: {
        return {
          ...changes,
          isOpen:
            isMobile && !props.disabled
              ? !isComponenteInMobileViewOpen
                ? true
                : false
              : changes.isOpen,
          ...(changes.selectedItem
            ? {
                inputValue: changes.selectedItem.label
              }
            : { inputValue: '' })
        };
      }
      case useCombobox.stateChangeTypes.ItemClick:
      case useCombobox.stateChangeTypes.InputKeyDownEnter:
        return {
          ...changes,
          ...(changes.selectedItem
            ? {
                inputValue: changes.selectedItem.label
              }
            : { inputValue: '' })
        };
      default:
        return changes;
    }
  }

  const items = props.openOnFocus
    ? getItems(props.options, inputValue, 0)
    : getItems(props.options, inputValue, props.minCharactersCountToOpen);

  const itemHeight = props.itemHeight || 40;

  const listRef = React.useRef(null);
  const isMobile = useIsMobileView();
  const inputRef = React.useRef<HTMLInputElement>(null);
  const doneBtnRef = React.useRef<HTMLButtonElement>(null);

  const rowVirtualizer = useVirtual({
    size: items.length,
    parentRef: listRef,
    overscan: 2
  });

  const {
    getInputProps,
    getItemProps,
    getLabelProps,
    getMenuProps,
    highlightedIndex,
    selectedItem,
    getComboboxProps,
    isOpen,
    getToggleButtonProps,
    openMenu,
    reset,
    selectItem,
    closeMenu
  } = useCombobox({
    items,
    stateReducer,
    inputValue,
    itemToString: (item) => item?.label || '',
    onInputValueChange: ({ inputValue: newValue = '' }) => {
      setInputValue(newValue);
    },
    onSelectedItemChange: ({ selectedItem }) => {
      updateDefaultSet(true);
      if (selectedItem !== null) {
        setWasMakedCleanAction(false);
      }
      if (!isMobile) {
        onChange(selectedItem ?? { label: '', value: '' });
      } else {
        if (
          props?.isPrepopulatedValue &&
          !props?.wasSetedPrepopulatedValue &&
          props?.setWasSetedPrepopulatedValue
        ) {
          onChange(selectedItem ?? { label: '', value: '' });
          props?.setWasSetedPrepopulatedValue(true);
        }
      }
    },
    scrollIntoView: () => {},
    onHighlightedIndexChange: ({ highlightedIndex }) => {
      if (highlightedIndex !== undefined) {
        rowVirtualizer.scrollToIndex(highlightedIndex);
      }
    }
  });

  useEffect(() => {
    if (!isDefaultSet && !inputValue) {
      if (props.defaultValue) {
        const element = props.options.find((elem) => elem.value === props.defaultValue);
        if (element) {
          selectItem(element);
        }
      }
    }
  }, [isDefaultSet, inputValue, props.defaultValue, props.options, selectItem]);

  function handleReset() {
    reset();
    if (!isMobile) {
      onChange({ label: '', value: '' });
      if (inputRef) {
        inputRef.current?.focus();
      }
    }
  }

  useEffect(() => {
    if (props.disabled) {
      handleReset();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.disabled]);

  let comboboxProps = getComboboxProps();
  delete comboboxProps['aria-expanded'];
  delete comboboxProps['role'];

  let inputProps = getInputProps({
    onFocus: () => {
      if (!isMobile && !isOpen && props.openOnFocus && !inputValue) {
        openMenu();
      }
    },
    type: 'text',
    placeholder: props.placeholder,
    ref: inputRef,
    onClick: () => {
      if (isMobile) {
        openMenu();
      }
    }
  });

  inputProps[
    'aria-labelledby'
  ] = `screenReaderMessageComboboxState-${id} ${inputProps['aria-labelledby']}`;

  const [isFocus, setIsFocus] = useState(false);

  const [isComponenteInMobileViewOpen, setIsComponenteInMobileViewOpen] = useState<boolean>(false);

  React.useEffect(() => {
    if (!isComponenteInMobileViewOpen) {
      setIsComponenteInMobileViewOpen(isOpen);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  React.useEffect(() => {
    if (isMobile && isComponenteInMobileViewOpen) {
      if (!wasMakedCleanAction) {
        setSaveInputValue(inputValue);
        setSaveSelectedItem(selectedItem);
      }
      if (saveSelectedItem && inputValue && saveSelectedItem.label === inputValue) {
        selectItem(saveSelectedItem);
      }
      setIsConfirmedAction(false);
      if (inputRef) {
        inputRef?.current?.focus();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isComponenteInMobileViewOpen]);

  const [accessibilityMessage, setAccessibilityMessage] = useState(false);
  const input = (
    <div
      sx={
        isComponenteInMobileViewOpen && isMobile ? { variant: 'layout.containerInputStyles' } : {}
      }
    >
      <VisuallyHidden aria-live="polite">
        {`${
          inputValue
            ? ''
            : props.descriptionIconCloseAction ??
              translate('cmp-combobox-value-cleared', 'Location cleared')
        }`}
      </VisuallyHidden>
      <VisuallyHidden aria-live="polite">
        {`${
          isOpen && !isFocus ? translate('cmp-combobox-expanded-menssage', 'Combobox expanded') : ''
        } `}
      </VisuallyHidden>
      <VisuallyHidden aria-live="polite">
        {`${
          isOpen && isFocus ? translate('cmp-combobox-expanded-menssage', 'Combobox expanded') : ''
        } `}
      </VisuallyHidden>
      <VisuallyHidden aria-live="polite">
        {`${
          accessibilityMessage
            ? translate('cmp-combobox-collapsed-menssage', 'Combobox collapsed')
            : ''
        } `}
      </VisuallyHidden>
      <div id={`screenReaderMessageComboboxState-${id}`} hidden>
        {!isOpen && isFocus
          ? translate('cmp-combobox-collapsed-menssage', 'Combobox collapsed')
          : ''}
      </div>
      <Inline
        alignY="center"
        data-disabled={props.disabled ? '' : undefined}
        data-error={props.hasError ? '' : undefined}
        data-active={props.active ? '' : undefined}
        sx={{ variant: 'layout.layoutStylesClosed' }}
      >
        <Stack sx={{ flex: '1' }}>
          <Label {...{ ...getLabelProps(), htmlFor: `${inputProps.id}-${id}` }}>
            {props.label}
          </Label>
          <div {...comboboxProps}>
            <input
              disabled={props.disabled}
              sx={{ variant: 'layout.inputStyles' }}
              {...{
                ...inputProps,
                id: `${inputProps.id}-${id}`
              }}
              onFocus={() => {
                inputProps.onFocus();
                setIsFocus(true);
              }}
              onBlur={(e) => {
                inputProps.onBlur(e);
                setIsFocus(false);
              }}
              onKeyDown={(e) => {
                if (e.keyCode === KEY_TAB) {
                  setAccessibilityMessage(true);
                } else {
                  setAccessibilityMessage(false);
                }
                inputProps.onKeyDown(e);
                if (props?.onKeyDown) {
                  props?.onKeyDown(e);
                }
              }}
            />
          </div>
        </Stack>
        {!inputValue && !props.minCharactersCountToOpen && (
          <Button
            disabled={props.disabled}
            variant="icon"
            type="button"
            {...getToggleButtonProps()}
            tabIndex={0}
            aria-label={props.ariaLabelIconToggle ?? 'Toggle view'}
          >
            <Icon
              icon={
                isOpen
                  ? props.rightIconOpen ?? 'up-arrow-fill'
                  : props.rightIcon ?? 'down-arrow-fill'
              }
              size={18}
            />
          </Button>
        )}
        {inputValue && (
          <Button
            disabled={props.disabled}
            variant="icon"
            onClick={
              isMobile
                ? () => {
                    setSaveInputValue(inputValue);
                    setSaveSelectedItem(selectedItem);
                    handleReset();
                    if (inputRef) {
                      inputRef.current?.focus();
                    }
                    setWasMakedCleanAction(true);
                  }
                : inputValue
                ? handleReset
                : openMenu
            }
            type="button"
            aria-label={props.ariaLabelIconClose ?? 'Clear'}
          >
            <Icon
              icon={props.iconClose ?? 'close'}
              size={18}
              sx={{ stroke: 'textSoft', strokeWidth: '60px' }}
            />
          </Button>
        )}
      </Inline>
    </div>
  );

  const footer = (
    <>
      <Inline align="end" sx={{ flex: '1' }}>
        <Button
          disabled={inputValue === ''}
          variant="text"
          onClick={() => {
            handleReset();
            if (doneBtnRef) {
              doneBtnRef.current?.focus();
            }
            setWasMakedCleanAction(true);
          }}
          type="button"
          data-testId="buttonFooterClear"
        >
          {translate('cmp-booking-location-selector-clear-label', 'Clear')}
        </Button>
        <Button
          variant="text"
          data-testId="buttonFooterDone"
          type="button"
          disabled={inputValue === '' && !wasMakedCleanAction}
          ref={doneBtnRef}
          onClick={() => {
            if (isOpen) {
              closeMenu();
            }
            setSaveInputValue(inputValue);
            setSaveSelectedItem(selectedItem);
            onChange(selectedItem ?? { label: '', value: '' });
            setIsConfirmedAction(true);
            if (!wasMakedCleanAction) {
              setIsComponenteInMobileViewOpen(false);
            }
            setWasMakedCleanAction(false);
          }}
        >
          {translate('cmp-booking-location-selector-done-label', 'Done')}
        </Button>
      </Inline>
    </>
  );
  const MenuWrapper = isMobile ? MobileFilterDialog : Fragment;

  const [lastHighlightedIndex, setLastHighlightedIndex] = React.useState(-1);

  React.useEffect(() => {
    if (highlightedIndex !== -1) {
      setLastHighlightedIndex(highlightedIndex);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [highlightedIndex]);

  React.useEffect(() => {
    setLastHighlightedIndex(-1);
  }, [isOpen]);

  const list = (
    <div
      style={{
        position: isMobile ? undefined : 'absolute',
        width: '100%',
        zIndex: 10
      }}
    >
      <ul
        {...getMenuProps({
          ref: listRef,
          style: styles.menu
        })}
        sx={{
          variant: isMobile ? 'menus.mobile' : 'menus.default',
          ...(isMobile && {
            width: '100%',
            height: '100%',
            maxHeight: '100%',
            boxShadow: 'none'
          })
        }}
      >
        {rowVirtualizer.virtualItems.length === 0 && (
          <li
            sx={{
              variant: isOpen && isMobile ? 'menuItems.mobileNoResult' : 'menuItems.default'
            }}
            style={{ height: '60px' }}
          >
            {translate('cmp-location-noresult', 'No result to show')}
          </li>
        )}
        <div
          sx={{
            variant: isMobile ? 'menuItems.mobileContainer' : 'menuItems.defaultContainer',
            height: `${rowVirtualizer.totalSize}px`
          }}
        >
          {rowVirtualizer.virtualItems.map((virtualRow, index) => (
            <li
              key={items[virtualRow.index].value}
              data-item-selected={
                selectedItem && selectedItem === items[virtualRow.index] ? true : undefined
              }
              data-item-first={
                !selectedItem &&
                index === 0 &&
                highlightedIndex === -1 &&
                lastHighlightedIndex === -1
                  ? true
                  : undefined
              }
              data-last-item-hover={
                !selectedItem &&
                highlightedIndex === -1 &&
                lastHighlightedIndex !== -1 &&
                index === lastHighlightedIndex
                  ? true
                  : undefined
              }
              data-item-highlighted={highlightedIndex === virtualRow.index ? true : undefined}
              sx={{
                variant: isOpen && isMobile ? 'menuItems.mobile' : 'menuItems.default'
              }}
              style={{
                position: 'absolute',
                top: 0,
                left: 0,
                marginTop: '0',
                marginBottom: '0',
                padding: '10px',
                minHeight: `${itemHeight}px`,
                width: '100%',
                transform: `translateY(${virtualRow.start}px)`
              }}
              {...getItemProps({
                index: virtualRow.index,
                item: items[virtualRow.index]
              })}
              ref={virtualRow.measureRef}
            >
              {items[virtualRow.index].label}
            </li>
          ))}
        </div>
      </ul>
    </div>
  );

  return (
    <Stack gap={1} sx={props.sx} className={props.className}>
      {isMobile && !isOpen && input}
      {isMobile && isOpen && <Input placeholder={props.placeholder} label={props.label} />}
      <MenuWrapper
        label="Filters"
        {...(isMobile && {
          isVisible: !isMobile ? isOpen : isComponenteInMobileViewOpen
        })}
      >
        {isMobile ? (
          <MobileHeader
            onBackButtonPress={() => {
              if (isOpen) {
                closeMenu();
              }
              selectItem(saveSelectedItem);
              if (!isConfirmedAction) {
                setInputValue(saveInputValue);
              }
              setIsConfirmedAction(false);
              setIsComponenteInMobileViewOpen(false);
              setWasMakedCleanAction(false);
            }}
            title={props.title}
          />
        ) : null}
        {!isMobile ? (
          <Box id="fr-combobox-container" p={isMobile ? 2 : 0} sx={{ position: 'relative' }}>
            {(!isMobile || isOpen) && input}
            {isOpen && list}
          </Box>
        ) : (
          <MobileBody>
            {(!isMobile || isComponenteInMobileViewOpen) && input}
            {isOpen && list}
          </MobileBody>
        )}
        {isMobile ? <MobileFooter align="end">{footer}</MobileFooter> : null}
      </MenuWrapper>
      {props.hasError && !props.disabled ? props.errorMessage : null}
    </Stack>
  );
}
